// Panel lateral (admin/auxiliar) con la lista de inspecciones: crear/editar
// (InspectionBuilderModal), asignar a empresas por modulo o puntuales
// (InspectionAssignModal), diligenciar (InspectionFillModal) y ver
// resultados (InspectionResultsModal).
import { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, ClipboardCheck, Send, BarChart2, Edit2, Trash2, Loader, FolderKanban, PlayCircle } from "lucide-react";
import { useConfirm } from "./ConfirmDialog";
import { useAuth } from "../context/AuthContext";
import { api } from "../api";
import InspectionBuilderModal from "./InspectionBuilderModal";
import InspectionAssignModal from "./InspectionAssignModal";
import InspectionResultsModal from "./InspectionResultsModal";
import InspectionModulesModal from "./InspectionModulesModal";
import InspectionFillModal from "./InspectionFillModal";

export default function InspectionsPanel({ onClose }) {
  const { isAdmin } = useAuth();
  const [inspections, setInspections] = useState([]);
  const [modules, setModules] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [builderTarget, setBuilderTarget] = useState(null); // null | "new" | inspection
  const [assignTarget, setAssignTarget] = useState(null); // inspection
  const [resultsTarget, setResultsTarget] = useState(null); // inspection
  const [fillTarget, setFillTarget] = useState(null); // inspection
  const [modulesOpen, setModulesOpen] = useState(false);
  const { confirm, ConfirmUI } = useConfirm();
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= 900);

  useEffect(() => {
    const syncMobile = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener("resize", syncMobile);
    return () => window.removeEventListener("resize", syncMobile);
  }, []);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.getInspections(),
      api.getInspectionModules(),
      api.getCompanies(),
      api.getInspectionAssignments(),
    ]).then(([inspectionsData, modulesData, companiesData, assignmentsData]) => {
      setInspections(Array.isArray(inspectionsData) ? inspectionsData : []);
      setModules(Array.isArray(modulesData) ? modulesData : []);
      setCompanies(Array.isArray(companiesData) ? companiesData : []);
      setAssignments(Array.isArray(assignmentsData) ? assignmentsData : []);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  const handleSaved = () => {
    setBuilderTarget(null);
    load();
  };

  const handleDelete = async (inspection) => {
    const ok = await confirm({
      title: "Eliminar inspeccion",
      message: `"${inspection.name}" sera eliminada. Los registros y documentos ya generados en las empresas NO se borran.`,
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    await api.deleteInspection(inspection.id);
    load();
  };

  const moduleName = (moduleId) => modules.find((m) => m.id === moduleId)?.name;

  const eligibleCompanies = (inspection) => {
    const moduleAssigned = new Set(assignments.filter((a) => a.module_id === inspection.module_id && inspection.module_id).map((a) => a.company_id));
    const directAssigned = new Set(assignments.filter((a) => a.inspection_id === inspection.id).map((a) => a.company_id));
    const ids = new Set([...moduleAssigned, ...directAssigned]);
    return companies.filter((c) => ids.has(c.id));
  };

  const groups = [
    ...modules.map((m) => ({ id: m.id, name: m.name, inspections: inspections.filter((i) => i.module_id === m.id) })),
    { id: null, name: "Sin modulo", inspections: inspections.filter((i) => !i.module_id || !modules.some((m) => m.id === i.module_id)) },
  ].filter((g) => g.inspections.length > 0);

  return ReactDOM.createPortal(
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.22 }}
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.32)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", zIndex: 99 }}
      />

      <motion.div
        initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -20, opacity: 0 }}
        transition={{ type: "spring", stiffness: 380, damping: 34, mass: 0.75 }}
        style={{
          position: "fixed", left: isMobile ? 0 : "var(--sidebar-w)", top: 0, bottom: 0, width: isMobile ? "100vw" : 560, zIndex: 100,
          display: "flex", flexDirection: "column",
          background: "rgba(246,246,248,0.97)",
          backdropFilter: "blur(48px) saturate(200%)", WebkitBackdropFilter: "blur(48px) saturate(200%)",
          borderRight: isMobile ? "none" : "1px solid rgba(0,0,0,0.08)",
          boxShadow: isMobile ? "none" : "4px 0 40px rgba(0,0,0,0.14), 1px 0 0 rgba(0,0,0,0.06)",
        }}>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px 18px", borderBottom: "1px solid rgba(0,0,0,0.06)", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "linear-gradient(135deg, #FFFBEB, #FEF3C7)", border: "1px solid #FDE68A", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ClipboardCheck size={20} style={{ color: "#B45309" }} />
            </div>
            <div>
              <h2 style={{ fontSize: "1.05rem", fontWeight: 700, letterSpacing: "-0.4px" }}>Inspecciones</h2>
              <p style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 1 }}>
                {inspections.length} inspeccion{inspections.length !== 1 ? "es" : ""} creada{inspections.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {isAdmin && (
              <button className="btn-icon" onClick={() => setModulesOpen(true)} title="Gestionar modulos">
                <FolderKanban size={18} />
              </button>
            )}
            <button className="btn-icon" onClick={onClose}><X size={18} /></button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 18 }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
              <Loader size={28} className="spin" opacity={0.4} />
            </div>
          ) : inspections.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "var(--text-muted)" }}>
              <ClipboardCheck size={40} opacity={0.25} />
              <p style={{ marginTop: 12, fontSize: "0.85rem" }}>No hay inspecciones creadas aun</p>
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.id ?? "none"}>
                {modules.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <p style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", color: "var(--text-muted)" }}>
                      {group.name}
                    </p>
                    <span style={{ fontSize: "0.68rem", color: "var(--text-muted)" }}>· {group.inspections.length}</span>
                  </div>
                )}
                <AnimatePresence>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {group.inspections.map((i, idx) => (
                      <motion.div key={i.id}
                        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: -20 }}
                        transition={{ type: "spring", stiffness: 360, damping: 28, delay: idx * 0.04 }}
                        style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 14, background: "rgba(255,255,255,0.7)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                          <div style={{ minWidth: 0 }}>
                            <p style={{ fontWeight: 600, fontSize: "0.92rem", letterSpacing: "-0.2px" }}>{i.name}</p>
                            {i.description && <p style={{ fontSize: "0.76rem", color: "var(--text-muted)", marginTop: 2 }}>{i.description}</p>}
                          </div>
                          <span style={{ fontSize: "0.68rem", fontWeight: 600, padding: "3px 9px", borderRadius: 20, background: "#FEF3C7", color: "#B45309", border: "1px solid #FDE68A", flexShrink: 0 }}>
                            {i.record_count} registro{i.record_count !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                          <button className="action-btn" onClick={() => setFillTarget(i)}>
                            <PlayCircle size={13} /> Diligenciar
                          </button>
                          <button className="action-btn" onClick={() => setResultsTarget(i)}>
                            <BarChart2 size={13} /> Resultados
                          </button>
                          {isAdmin && (
                            <>
                              <button className="action-btn" onClick={() => setAssignTarget(i)}>
                                <Send size={13} /> Asignar
                              </button>
                              <button className="action-btn" onClick={() => setBuilderTarget(i)}>
                                <Edit2 size={13} /> Editar
                              </button>
                              <button className="action-btn danger" onClick={() => handleDelete(i)}>
                                <Trash2 size={13} />
                              </button>
                            </>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </AnimatePresence>
              </div>
            ))
          )}
        </div>

        {isAdmin && (
          <div style={{ padding: "16px 24px", borderTop: "1px solid rgba(0,0,0,0.06)", background: "rgba(245,245,247,0.6)", flexShrink: 0 }}>
            <button className="btn-secondary" onClick={() => setBuilderTarget("new")}
              style={{ width: "100%", justifyContent: "center", padding: "11px" }}>
              <Plus size={16} /> Nueva inspeccion
            </button>
          </div>
        )}
      </motion.div>

      <AnimatePresence>
        {builderTarget && (
          <InspectionBuilderModal
            key="builder"
            inspection={builderTarget === "new" ? null : builderTarget}
            onClose={() => setBuilderTarget(null)}
            onSaved={handleSaved}
          />
        )}
        {assignTarget && (
          <InspectionAssignModal key="assign" inspection={assignTarget} moduleName={moduleName(assignTarget.module_id)} onClose={() => { setAssignTarget(null); load(); }} />
        )}
        {resultsTarget && (
          <InspectionResultsModal key="results" inspection={resultsTarget} onClose={() => setResultsTarget(null)} />
        )}
        {fillTarget && (
          <InspectionFillModal key="fill" inspection={fillTarget} companies={eligibleCompanies(fillTarget)}
            onClose={() => setFillTarget(null)} onSubmitted={load} />
        )}
        {modulesOpen && (
          <InspectionModulesModal key="modules" onClose={() => setModulesOpen(false)} onChanged={load} />
        )}
      </AnimatePresence>
      {ConfirmUI}
    </>,
    document.body
  );
}
